import React, { createContext, useContext, useState } from 'react';

// --- Models ---
const initialPackages = [
  { id: 'pkg-2', name: '2 Users', userLimit: 2, price: 49 },
  { id: 'pkg-3', name: '3 Users', userLimit: 3, price: 69 },
  { id: 'pkg-5', name: '5 Users', userLimit: 5, price: 99 },
];

const initialSalons = [
  {
    id: 'salon-1',
    name: 'Demo Salon',
    packageId: 'pkg-3',
    users: [
      { id: 'admin-1', name: 'Admin', email: 'manager@postrics.com', role: 'admin', status: 'active' },
      { id: 'user-1', name: 'User 1', email: 'user1@postrics.com', role: 'user', status: 'active' },
      { id: 'user-2', name: 'User 2', email: 'user2@postrics.com', role: 'user', status: 'active' },
    ],
    billing: { status: 'paid', nextDue: '2025-08-01' },
    supportTickets: [],
    status: 'active',
  },
];

const initialSuperAdmin = {
  id: 'superadmin-1',
  name: 'Super Admin',
  email: 'admin@postrics.com',
  role: 'super_admin',
  status: 'active',
};

// --- Context ---
const GlobalStateContext = createContext();

export const GlobalStateProvider = ({ children }) => {
  const [user, setUser] = useState(null); // current logged in user
  const [salons, setSalons] = useState(initialSalons);
  const [packages, setPackages] = useState(initialPackages);
  const [superAdmin] = useState(initialSuperAdmin);

  // --- RBAC utility ---
  const hasPermission = (role, action) => {
    const permissions = {
      super_admin: ['manage_packages', 'manage_billing', 'view_salons', 'all'],
      admin: ['manage_products', 'manage_employees', 'view_salon', 'all'],
      user: ['view_basic', 'all'],
    };
    return permissions[role]?.includes(action) || permissions[role]?.includes('all');
  };

  // --- Auth ---
  const login = (email, password, role) => {
    // Check if it's a super admin login
    if (role === 'super_admin') {
      if (email === superAdmin.email && password === 'admin123') {
        setUser(superAdmin);
        return { success: true, user: superAdmin };
      }
      return { success: false, error: 'Invalid super admin credentials' };
    }
    
    // Check for regular salon users
    for (const salon of salons) {
      const found = salon.users.find(u => u.email === email && password === 'user123' && u.role === role);
      if (found) {
        setUser({ ...found, salonId: salon.id, packageId: salon.packageId });
        return { success: true, user: { ...found, salonId: salon.id, packageId: salon.packageId } };
      }
    }
    return { success: false, error: 'Invalid credentials for selected role' };
  };

  const logout = () => setUser(null);

  // --- Package Management (Super Admin) ---
  const createPackage = (pkg) => setPackages(pkgs => [...pkgs, pkg]);
  const assignPackageToSalon = (salonId, packageId) => {
    setSalons(salons => salons.map(s => s.id === salonId ? { ...s, packageId } : s));
  };

  // --- User Management (Admin) ---
  const addUserToSalon = (salonId, userObj) => {
    setSalons(salons => salons.map(s => {
      if (s.id === salonId) {
        const pkg = packages.find(p => p.id === s.packageId);
        if (s.users.length < pkg.userLimit) {
          return { ...s, users: [...s.users, userObj] };
        }
        return s; // user limit reached
      }
      return s;
    }));
  };

  // --- Support ---
  const addSupportTicket = (salonId, ticket) => {
    setSalons(salons => salons.map(s =>
      s.id === salonId ? { ...s, supportTickets: [...s.supportTickets, ticket] } : s
    ));
  };

  return (
    <GlobalStateContext.Provider value={{
      user, setUser, salons, setSalons, packages, setPackages, superAdmin,
      login, logout, hasPermission, createPackage, assignPackageToSalon, addUserToSalon, addSupportTicket
    }}>
      {children}
    </GlobalStateContext.Provider>
  );
};

export const useGlobalState = () => useContext(GlobalStateContext);